import Phaser from 'phaser';
import * as THREE from 'three';
import { GAME_HEIGHT, GAME_WIDTH } from '../config/game';
import type { EnemyToken } from '../entities/enemy';
import type { PartnerVisualSnapshot, ProjectileVisualSnapshot } from '../systems/PartnerSystem';
import type { DropVisualSnapshot } from '../systems/MaterialSystem';
import type { RunState } from '../systems/runState';
import type { PartnerType } from '../systems/shopCatalog';

type BattleViewState = {
  player: Phaser.GameObjects.Arc;
  enemies: EnemyToken[];
  partners: PartnerVisualSnapshot[];
  projectiles: ProjectileVisualSnapshot[];
  drops: DropVisualSnapshot[];
  snapshot: ReturnType<RunState['snapshot']>;
};

type DamageNumber = {
  element: HTMLDivElement;
  world: THREE.Vector3;
  bornAt: number;
  duration: number;
  drift: number;
};

type TimedMesh = {
  mesh: THREE.Object3D;
  bornAt: number;
  duration: number;
  update: (age: number, t: number) => void;
};

type EnemyMeshEntry = {
  group: THREE.Group;
  body: THREE.Mesh;
  lastHp: number;
  flashUntil: number;
};

type PartnerMeshEntry = {
  group: THREE.Group;
  type: PartnerType;
  level: number;
};

type DropMeshEntry = {
  mesh: THREE.Mesh;
};

type ProjectileMeshEntry = {
  group: THREE.Group;
};

type ThreeBattleMetrics = {
  pixelRatio: number;
  calls: number;
  triangles: number;
  geometries: number;
  textures: number;
  enemies: number;
  partners: number;
  drops: number;
  projectiles: number;
  transientMeshes: number;
  damageNumbers: number;
};

const WORLD_WIDTH = 26;
const WORLD_HEIGHT = 36;
const ARENA_TOP = 86;
const ARENA_BOTTOM = GAME_HEIGHT;
const ARENA_HEIGHT = ARENA_BOTTOM - ARENA_TOP;
const MOBILE_MAX_PIXEL_RATIO = 1.5;
const DESKTOP_MAX_PIXEL_RATIO = 1.75;
const MAX_DAMAGE_NUMBERS = 48;
const MAX_TRANSIENT_MESHES = 90;

const COLORS = {
  void: 0x070908,
  floorA: 0x151710,
  floorB: 0x1c1a16,
  floorLine: 0x373123,
  border: 0x5d4b2e,
  playerBody: 0xf0a24b,
  playerHair: 0xffc96a,
  playerCape: 0x32265d,
  damageWhite: '#fff7de',
  damageCrit: '#ffe35a',
  material: 0xbef8ff,
  danger: 0xca2f54,
};

const partnerColors: Record<PartnerType, number> = {
  gunner: 0x72a9ff,
  sword: 0xffd45f,
  mage: 0xc78cff,
  nurse: 0x7dffae,
  engineer: 0xffa65a,
  assassin: 0xff5d7e,
  tank: 0xb8c5c9,
  summoner: 0x67e4dd,
};

function colorNumberToCss(color: number) {
  return `#${color.toString(16).padStart(6, '0')}`;
}

function isMobileBattleViewport() {
  return window.innerWidth < 700 || navigator.maxTouchPoints > 1;
}

function resolveBattlePixelRatio() {
  const dpr = window.devicePixelRatio || 1;
  return Math.max(1, Math.min(dpr, isMobileBattleViewport() ? MOBILE_MAX_PIXEL_RATIO : DESKTOP_MAX_PIXEL_RATIO));
}

function disposeMaterial(material: THREE.Material | THREE.Material[]) {
  if (Array.isArray(material)) {
    material.forEach((item) => item.dispose());
    return;
  }
  material.dispose();
}

function disposeObject(object: THREE.Object3D) {
  object.traverse((child) => {
    const mesh = child as THREE.Mesh;
    if (mesh.geometry) {
      mesh.geometry.dispose();
    }
    if (mesh.material) {
      disposeMaterial(mesh.material);
    }
  });
}

export class ThreeBattleView {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(45, 1, 0.1, 140);
  private readonly root = new THREE.Group();
  private readonly player = new THREE.Group();
  private readonly enemyMeshes = new Map<EnemyToken, EnemyMeshEntry>();
  private readonly partnerMeshes = new Map<number, PartnerMeshEntry>();
  private readonly dropMeshes = new Map<number, DropMeshEntry>();
  private readonly projectileMeshes = new Map<Phaser.GameObjects.Arc, ProjectileMeshEntry>();
  private readonly transientMeshes: TimedMesh[] = [];
  private readonly damageNumbers: DamageNumber[] = [];
  private readonly hud: HTMLDivElement;
  private readonly materialValue: HTMLDivElement;
  private readonly timerValue: HTMLDivElement;
  private readonly waveValue: HTMLDivElement;
  private readonly hpFill: HTMLDivElement;
  private readonly hpValue: HTMLDivElement;
  private readonly levelValue: HTMLDivElement;
  private readonly partnerRail: HTMLDivElement;
  private readonly damageLayer: HTMLDivElement;
  private readonly resizeObserver: ResizeObserver;
  private readonly tmp = new THREE.Vector3();
  private cameraTarget = new THREE.Vector3(0, 0, 0);
  private cameraPosition = new THREE.Vector3(0, 16, 16);
  private lastPlayerWorld = new THREE.Vector3(0, 0, 0);
  private partnerRailSignature = '';
  private lastHudUpdateAt = 0;
  private disposed = false;

  constructor(private readonly phaserScene: Phaser.Scene) {
    const app = document.getElementById('app');
    if (!app) {
      throw new Error('Missing #app mount for Three battle view');
    }

    this.renderer = new THREE.WebGLRenderer({
      antialias: !isMobileBattleViewport(),
      alpha: false,
      powerPreference: 'high-performance',
      precision: isMobileBattleViewport() ? 'mediump' : 'highp',
    });
    this.renderer.setPixelRatio(resolveBattlePixelRatio());
    this.renderer.setClearColor(COLORS.void, 1);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.domElement.className = 'three-battle-canvas';
    app.prepend(this.renderer.domElement);
    document.body.classList.add('three-battle-active');

    this.hud = document.createElement('div');
    this.hud.className = 'three-battle-hud';
    this.hud.innerHTML = `
      <div class="three-hud-material"><span class="three-hud-label">$</span><span data-role="materials">0</span></div>
      <div class="three-hud-timer"><span data-role="timer">20</span><small data-role="wave">w1</small></div>
      <div class="three-hud-vitals">
        <div class="three-hud-level">Lv. <span data-role="level">1</span></div>
        <div class="three-hud-bar"><span data-role="hp-fill"></span></div>
        <div class="three-hud-hp"><span data-role="hp">100 / 100</span></div>
      </div>
      <div class="three-hud-partners" data-role="partners"></div>
    `;
    app.appendChild(this.hud);
    this.materialValue = this.getHudNode('materials');
    this.timerValue = this.getHudNode('timer');
    this.waveValue = this.getHudNode('wave');
    this.hpFill = this.getHudNode('hp-fill');
    this.hpValue = this.getHudNode('hp');
    this.levelValue = this.getHudNode('level');
    this.partnerRail = this.getHudNode('partners');

    this.damageLayer = document.createElement('div');
    this.damageLayer.className = 'three-damage-layer';
    app.appendChild(this.damageLayer);

    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(app);
    this.resize();
    this.buildWorld();
    this.buildPlayer();
    this.bindEvents();
  }

  update(state: BattleViewState, timeMs: number) {
    if (this.disposed) {
      return;
    }

    this.hidePhaserWorld(state);
    this.updatePlayer(state.player, timeMs);
    this.updateEnemies(state.enemies, timeMs);
    this.updatePartners(state.partners, timeMs);
    this.updateDrops(state.drops, timeMs);
    this.updateProjectiles(state.projectiles, timeMs);
    this.updateTransientMeshes(timeMs);
    this.updateDamageNumbers(timeMs);
    if (timeMs - this.lastHudUpdateAt >= 100 || state.snapshot.phase !== 'playing') {
      this.updateHud(state.snapshot, state.partners);
      this.lastHudUpdateAt = timeMs;
    }
    this.updateCamera(state.player, timeMs);
    this.renderer.render(this.scene, this.camera);
    this.publishMetrics();
  }

  destroy() {
    if (this.disposed) {
      return;
    }
    this.disposed = true;
    this.resizeObserver.disconnect();
    this.clearDynamicMeshes();
    disposeObject(this.root);
    this.scene.clear();
    this.renderer.dispose();
    this.renderer.domElement.remove();
    this.hud.remove();
    this.damageLayer.remove();
    document.body.classList.remove('three-battle-active');
    for (const entry of this.damageNumbers) {
      entry.element.remove();
    }
    this.damageNumbers.length = 0;
    window.__threeBattleMetrics = undefined;
  }

  private getHudNode(role: string) {
    const node = this.hud.querySelector(`[data-role="${role}"]`);
    if (!node) {
      throw new Error(`Missing Three HUD node: ${role}`);
    }
    return node as HTMLDivElement;
  }

  private bindEvents() {
    this.phaserScene.events.on('enemy-damaged', this.handleEnemyDamaged, this);
    this.phaserScene.events.on('enemy-died', this.handleEnemyDied, this);
    this.phaserScene.events.on('boss-died', this.handleBossDied, this);
    this.phaserScene.events.on('material-visual-picked', this.handleMaterialPicked, this);
    this.phaserScene.events.on('visual-slash', this.handleSlash, this);
    this.phaserScene.events.on('visual-pulse', this.handlePulse, this);
    this.phaserScene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.phaserScene.events.off('enemy-damaged', this.handleEnemyDamaged, this);
      this.phaserScene.events.off('enemy-died', this.handleEnemyDied, this);
      this.phaserScene.events.off('boss-died', this.handleBossDied, this);
      this.phaserScene.events.off('material-visual-picked', this.handleMaterialPicked, this);
      this.phaserScene.events.off('visual-slash', this.handleSlash, this);
      this.phaserScene.events.off('visual-pulse', this.handlePulse, this);
      this.destroy();
    });
  }

  private buildWorld() {
    this.scene.fog = new THREE.FogExp2(COLORS.void, 0.026);
    this.scene.add(this.root);
    this.root.add(new THREE.HemisphereLight(0xd8c29a, 0x070908, 1.8));

    const keyLight = new THREE.DirectionalLight(0xffefc5, 2.8);
    keyLight.position.set(-7, 15, 7);
    this.root.add(keyLight);

    const rimLight = new THREE.PointLight(0x5c80ff, 1.2, 24);
    rimLight.position.set(8, 4, -12);
    this.root.add(rimLight);

    const tileSize = 2;
    const tileGeo = new THREE.BoxGeometry(tileSize, 0.16, tileSize);
    const matA = new THREE.MeshStandardMaterial({ color: COLORS.floorA, roughness: 0.88, metalness: 0.04 });
    const matB = new THREE.MeshStandardMaterial({ color: COLORS.floorB, roughness: 0.9, metalness: 0.03 });
    const tileColumns = Math.ceil(WORLD_WIDTH / tileSize);
    const tileRows = Math.ceil(WORLD_HEIGHT / tileSize);
    const tileCount = tileColumns * tileRows;
    const tilesA = new THREE.InstancedMesh(tileGeo, matA, tileCount);
    const tilesB = new THREE.InstancedMesh(tileGeo, matB, tileCount);
    const matrix = new THREE.Matrix4();
    let countA = 0;
    let countB = 0;
    for (let x = -WORLD_WIDTH / 2; x < WORLD_WIDTH / 2; x += tileSize) {
      for (let z = -WORLD_HEIGHT / 2; z < WORLD_HEIGHT / 2; z += tileSize) {
        matrix.makeTranslation(x + tileSize / 2, -0.1, z + tileSize / 2);
        if (((Math.round(x / tileSize) + Math.round(z / tileSize)) & 1) === 0) {
          tilesA.setMatrixAt(countA, matrix);
          countA += 1;
        } else {
          tilesB.setMatrixAt(countB, matrix);
          countB += 1;
        }
      }
    }
    tilesA.count = countA;
    tilesB.count = countB;
    this.root.add(tilesA, tilesB);

    const borderMat = new THREE.MeshStandardMaterial({ color: COLORS.border, roughness: 0.72, metalness: 0.1 });
    const borderLong = new THREE.BoxGeometry(WORLD_WIDTH + 0.8, 0.58, 0.38);
    const borderShort = new THREE.BoxGeometry(0.38, 0.58, WORLD_HEIGHT + 0.8);
    const top = new THREE.Mesh(borderLong, borderMat);
    top.position.set(0, 0.12, -WORLD_HEIGHT / 2 - 0.2);
    const bottom = top.clone();
    bottom.position.z = WORLD_HEIGHT / 2 + 0.2;
    const left = new THREE.Mesh(borderShort, borderMat);
    left.position.set(-WORLD_WIDTH / 2 - 0.2, 0.12, 0);
    const right = left.clone();
    right.position.x = WORLD_WIDTH / 2 + 0.2;
    this.root.add(top, bottom, left, right);

    const slabMat = new THREE.MeshStandardMaterial({ color: 0x030605, roughness: 0.96, metalness: 0.0 });
    const slabGeo = new THREE.BoxGeometry(5.5, 8, 18);
    const leftSlab = new THREE.Mesh(slabGeo, slabMat);
    leftSlab.position.set(-18, 2.5, -5);
    leftSlab.rotation.z = -0.18;
    const rightSlab = new THREE.Mesh(slabGeo, slabMat);
    rightSlab.position.set(18, 2.5, -4);
    rightSlab.rotation.z = 0.2;
    const rearWall = new THREE.Mesh(new THREE.BoxGeometry(WORLD_WIDTH * 0.8, 6, 1.5), slabMat);
    rearWall.position.set(0, 2.8, -22);
    this.root.add(leftSlab, rightSlab, rearWall);

    const markMat = new THREE.MeshBasicMaterial({ color: COLORS.danger, transparent: true, opacity: 0.32 });
    for (const [x, z, rot] of [
      [-9, -8, 0.2],
      [8, -10, -0.1],
      [-7, 9, -0.3],
      [10, 8, 0.25],
      [0, 12, -0.05],
    ]) {
      const mark = this.makeCrossMark(markMat);
      mark.position.set(x, 0.025, z);
      mark.rotation.y = rot;
      this.root.add(mark);
    }

    const starGeo = new THREE.OctahedronGeometry(0.035, 0);
    const starMat = new THREE.MeshBasicMaterial({ color: 0xd7fbff, transparent: true, opacity: 0.45 });
    const stars = new THREE.InstancedMesh(starGeo, starMat, 70);
    for (let i = 0; i < 70; i += 1) {
      matrix.makeTranslation((Math.random() - 0.5) * 54, 1 + Math.random() * 12, -24 + Math.random() * 55);
      stars.setMatrixAt(i, matrix);
    }
    this.root.add(stars);
  }

  private makeCrossMark(material: THREE.Material) {
    const group = new THREE.Group();
    const geo = new THREE.BoxGeometry(1.45, 0.035, 0.24);
    const a = new THREE.Mesh(geo, material);
    const b = new THREE.Mesh(geo, material);
    a.rotation.y = Math.PI / 4;
    b.rotation.y = -Math.PI / 4;
    group.add(a, b);
    return group;
  }

  private buildPlayer() {
    const bodyMat = new THREE.MeshStandardMaterial({ color: COLORS.playerBody, roughness: 0.58, emissive: 0x3b1604, emissiveIntensity: 0.18 });
    const hairMat = new THREE.MeshStandardMaterial({ color: COLORS.playerHair, roughness: 0.5, emissive: 0x301600, emissiveIntensity: 0.2 });
    const capeMat = new THREE.MeshStandardMaterial({ color: COLORS.playerCape, roughness: 0.7, emissive: 0x120c38, emissiveIntensity: 0.35 });
    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.36, 0.55, 4, 8), bodyMat);
    body.position.y = 0.72;
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.42, 14, 10), hairMat);
    head.position.y = 1.28;
    const cape = new THREE.Mesh(new THREE.BoxGeometry(0.64, 0.5, 0.12), capeMat);
    cape.position.set(0, 0.78, 0.28);
    const legMat = new THREE.MeshStandardMaterial({ color: 0x26215d, roughness: 0.7 });
    const legGeo = new THREE.BoxGeometry(0.18, 0.5, 0.18);
    const leftLeg = new THREE.Mesh(legGeo, legMat);
    leftLeg.position.set(-0.18, 0.22, 0);
    const rightLeg = leftLeg.clone();
    rightLeg.position.x = 0.18;
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.62, 0.68, 36),
      new THREE.MeshBasicMaterial({ color: 0xffcf6e, transparent: true, opacity: 0.28, side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.035;
    this.player.add(ring, body, head, cape, leftLeg, rightLeg);
    this.root.add(this.player);
  }

  private updatePlayer(player: Phaser.GameObjects.Arc, timeMs: number) {
    const world = this.toWorld(player.x, player.y);
    const dx = world.x - this.lastPlayerWorld.x;
    const dz = world.z - this.lastPlayerWorld.z;
    this.player.position.copy(world);
    this.player.position.y = 0;
    if (Math.abs(dx) + Math.abs(dz) > 0.001) {
      const targetRot = Math.atan2(dx, dz);
      this.player.rotation.y = Phaser.Math.Angle.RotateTo(this.player.rotation.y, targetRot, 0.18);
    }
    const runBob = Math.sin(timeMs * 0.018) * Math.min(0.08, Math.hypot(dx, dz) * 1.6);
    this.player.position.y = runBob;
    this.lastPlayerWorld.copy(world);
  }

  private updateEnemies(enemies: EnemyToken[], timeMs: number) {
    const active = new Set(enemies);
    for (const [enemy, entry] of this.enemyMeshes) {
      if (!active.has(enemy)) {
        this.root.remove(entry.group);
        disposeObject(entry.group);
        this.enemyMeshes.delete(enemy);
      }
    }

    for (const enemy of enemies) {
      let entry = this.enemyMeshes.get(enemy);
      if (!entry) {
        entry = this.createEnemyMesh(enemy);
        this.enemyMeshes.set(enemy, entry);
        this.root.add(entry.group);
      }
      const world = this.toWorld(enemy.x, enemy.y);
      const isBoss = enemy.getData('bossWave') !== undefined || enemy.radius >= 24;
      const elite = enemy.getData('elite') === true;
      entry.group.position.set(world.x, 0, world.z);
      entry.group.rotation.y += (isBoss ? 0.006 : 0.014) * (enemy.turn || 1);
      const scale = (enemy.radius / 13) * (elite ? 1.18 : 1) * (isBoss ? 1.15 : 1);
      entry.group.scale.setScalar(scale);
      const hp = enemy.hp;
      if (hp < entry.lastHp) {
        entry.flashUntil = timeMs + 85;
      }
      entry.lastHp = hp;
      const material = entry.body.material as THREE.MeshStandardMaterial;
      if (timeMs < entry.flashUntil) {
        material.color.setHex(0xffffff);
        material.emissive.setHex(0xffffff);
        material.emissiveIntensity = 0.55;
      } else {
        material.color.setHex(this.enemyColor(enemy));
        material.emissive.setHex(elite ? 0x553200 : isBoss ? 0x4c1120 : 0x260606);
        material.emissiveIntensity = elite || isBoss ? 0.35 : 0.18;
      }
    }
  }

  private createEnemyMesh(enemy: EnemyToken): EnemyMeshEntry {
    const group = new THREE.Group();
    const isBoss = enemy.getData('bossWave') !== undefined || enemy.radius >= 24;
    const geo = isBoss
      ? new THREE.DodecahedronGeometry(0.92, 0)
      : enemy.kind === 'shooter'
        ? new THREE.OctahedronGeometry(0.7, 0)
        : enemy.kind === 'flanker'
          ? new THREE.ConeGeometry(0.58, 1.12, 5)
          : new THREE.CylinderGeometry(0.48, 0.62, 0.92, 6);
    const mat = new THREE.MeshStandardMaterial({
      color: this.enemyColor(enemy),
      roughness: 0.62,
      metalness: 0.05,
      emissive: 0x260606,
      emissiveIntensity: 0.18,
    });
    const body = new THREE.Mesh(geo, mat);
    body.position.y = isBoss ? 1.0 : 0.55;
    group.add(body);
    const shadow = new THREE.Mesh(
      new THREE.CircleGeometry(isBoss ? 1.05 : 0.58, 24),
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.32, side: THREE.DoubleSide }),
    );
    shadow.rotation.x = -Math.PI / 2;
    shadow.position.y = 0.025;
    group.add(shadow);
    return { group, body, lastHp: enemy.hp, flashUntil: 0 };
  }

  private enemyColor(enemy: EnemyToken) {
    if (enemy.getData('elite') === true) return 0xffcf4a;
    if (enemy.getData('bossWave') !== undefined || enemy.radius >= 24) return 0xc9435d;
    if (enemy.kind === 'shooter') return 0x438dca;
    if (enemy.kind === 'flanker') return 0x8058d6;
    if (enemy.kind === 'burster') return 0xef7e38;
    return 0xd84d45;
  }

  private updatePartners(partners: PartnerVisualSnapshot[], timeMs: number) {
    const activeKeys = new Set<number>();
    partners.forEach((partner, index) => {
      activeKeys.add(index);
      let entry = this.partnerMeshes.get(index);
      if (!entry || entry.type !== partner.type || entry.level !== partner.level) {
        if (entry) {
          this.root.remove(entry.group);
          disposeObject(entry.group);
        }
        entry = this.createPartnerMesh(partner.type, partner.level);
        this.partnerMeshes.set(index, entry);
        this.root.add(entry.group);
      }
      const world = this.toWorld(partner.x, partner.y);
      entry.group.position.set(world.x, 0.08 + Math.sin(timeMs * 0.006 + index) * 0.08, world.z);
      entry.group.rotation.y += 0.025 + partner.level * 0.002;
      entry.group.scale.setScalar(1 + (partner.level - 1) * 0.09);
    });

    for (const [key, entry] of this.partnerMeshes) {
      if (!activeKeys.has(key)) {
        this.root.remove(entry.group);
        disposeObject(entry.group);
        this.partnerMeshes.delete(key);
      }
    }
  }

  private createPartnerMesh(type: PartnerType, level: number): PartnerMeshEntry {
    const color = partnerColors[type];
    const group = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({
      color,
      roughness: 0.45,
      metalness: 0.08,
      emissive: color,
      emissiveIntensity: 0.55,
      transparent: true,
      opacity: 0.92,
    });
    const core =
      type === 'gunner' ? new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.28, 0.34), mat) :
      type === 'sword' ? new THREE.Mesh(new THREE.BoxGeometry(0.24, 0.22, 0.95), mat) :
      type === 'mage' ? new THREE.Mesh(new THREE.OctahedronGeometry(0.42, 0), mat) :
      type === 'nurse' ? new THREE.Mesh(new THREE.SphereGeometry(0.34, 12, 8), mat) :
      type === 'engineer' ? new THREE.Mesh(new THREE.CylinderGeometry(0.38, 0.38, 0.36, 6), mat) :
      type === 'assassin' ? new THREE.Mesh(new THREE.ConeGeometry(0.34, 0.9, 4), mat) :
      type === 'tank' ? new THREE.Mesh(new THREE.BoxGeometry(0.75, 0.58, 0.2), mat) :
      new THREE.Mesh(new THREE.TetrahedronGeometry(0.48, 0), mat);
    core.position.y = 0.55;
    group.add(core);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.42 + level * 0.04, 0.47 + level * 0.04, 20),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: Math.min(0.42, 0.18 + level * 0.05), side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.08;
    group.add(ring);
    return { group, type, level };
  }

  private updateDrops(drops: DropVisualSnapshot[], timeMs: number) {
    const activeIds = new Set<number>();
    for (const drop of drops) {
      activeIds.add(drop.id);
      let entry = this.dropMeshes.get(drop.id);
      if (!entry) {
        entry = this.createDropMesh(drop.kind);
        this.dropMeshes.set(drop.id, entry);
        this.root.add(entry.mesh);
      }
      const world = this.toWorld(drop.x, drop.y);
      entry.mesh.position.set(world.x, 0.22 + Math.sin(timeMs * 0.008 + drop.id) * 0.06, world.z);
      entry.mesh.rotation.x += 0.035;
      entry.mesh.rotation.y += 0.025;
      entry.mesh.scale.setScalar(drop.state === 'magnetic' ? 1.18 : 1);
    }
    for (const [id, entry] of this.dropMeshes) {
      if (!activeIds.has(id)) {
        this.root.remove(entry.mesh);
        disposeObject(entry.mesh);
        this.dropMeshes.delete(id);
      }
    }
  }

  private createDropMesh(kind: DropVisualSnapshot['kind']): DropMeshEntry {
    const color = kind === 'material' ? COLORS.material : kind === 'heal-orb' ? 0x78ff9e : kind === 'xp-orb' ? 0xffdf62 : kind === 'haste-orb' ? 0x64caff : kind === 'material-crate' ? 0xffbc5a : 0xd488ff;
    const geo = kind === 'material'
      ? new THREE.OctahedronGeometry(0.16, 0)
      : kind === 'material-crate' || kind === 'upgrade-chest'
        ? new THREE.BoxGeometry(0.38, 0.32, 0.38)
        : new THREE.OctahedronGeometry(0.24, 0);
    const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({
      color,
      roughness: 0.28,
      emissive: color,
      emissiveIntensity: kind === 'material' ? 0.7 : 0.95,
    }));
    return { mesh };
  }

  private updateProjectiles(projectiles: ProjectileVisualSnapshot[], timeMs: number) {
    const activeRefs = new Set<Phaser.GameObjects.Arc>();
    for (const projectile of projectiles) {
      activeRefs.add(projectile.ref);
      let entry = this.projectileMeshes.get(projectile.ref);
      if (!entry) {
        entry = this.createProjectileMesh(projectile.color, projectile.radius);
        this.projectileMeshes.set(projectile.ref, entry);
        this.root.add(entry.group);
      }
      const world = this.toWorld(projectile.x, projectile.y);
      entry.group.position.set(world.x, 0.5, world.z);
      entry.group.rotation.y = projectile.rotation;
      entry.group.scale.setScalar(Math.max(0.7, projectile.radius / 4));
      const pulse = 1 + Math.sin(timeMs * 0.025) * 0.08;
      entry.group.children[0].scale.setScalar(pulse);
    }
    for (const [ref, entry] of this.projectileMeshes) {
      if (!activeRefs.has(ref)) {
        this.root.remove(entry.group);
        disposeObject(entry.group);
        this.projectileMeshes.delete(ref);
      }
    }
  }

  private createProjectileMesh(color: number, radius: number): ProjectileMeshEntry {
    const group = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.25, roughness: 0.25 });
    const core = new THREE.Mesh(new THREE.SphereGeometry(0.12 + radius * 0.015, 12, 8), mat);
    const trail = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.08, 0.82),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.32 }),
    );
    trail.position.z = 0.38;
    group.add(core, trail);
    return { group };
  }

  private handleEnemyDamaged(payload: { amount?: number; x?: number; y?: number; source?: PartnerType | 'relic' } | number) {
    const amount = typeof payload === 'number' ? payload : payload.amount;
    if (!amount || typeof payload === 'number' || payload.x === undefined || payload.y === undefined) {
      return;
    }
    this.spawnDamageNumber(payload.x, payload.y, amount, payload.source);
  }

  private handleEnemyDied(x: number, y: number) {
    const world = this.toWorld(x, y);
    for (let i = 0; i < 6; i += 1) {
      this.spawnShard(world, 0xd84d45, i);
    }
  }

  private handleBossDied(payload: { x: number; y: number }) {
    const world = this.toWorld(payload.x, payload.y);
    this.spawnRing(world, 0xffd35a, 0.6, 3.2, 620);
    for (let i = 0; i < 14; i += 1) {
      this.spawnShard(world, 0xffd35a, i);
    }
  }

  private handleMaterialPicked(payload: { x: number; y: number; kind: string }) {
    const world = this.toWorld(payload.x, payload.y);
    this.spawnRing(world, payload.kind === 'material' ? COLORS.material : 0xffd35a, 0.25, 0.9, 280);
  }

  private handleSlash(payload: { x: number; y: number; angle: number; range: number; color: number }) {
    const world = this.toWorld(payload.x, payload.y);
    const length = Math.max(1.5, payload.range / 22);
    const slash = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.2, length),
      new THREE.MeshBasicMaterial({ color: payload.color, transparent: true, opacity: 0.95 }),
    );
    slash.position.set(world.x + Math.cos(payload.angle) * length * 0.35, 0.72, world.z + Math.sin(payload.angle) * length * 0.35);
    slash.rotation.y = Math.PI / 2 - payload.angle;
    this.root.add(slash);
    this.transientMeshes.push({
      mesh: slash,
      bornAt: this.phaserScene.time.now,
      duration: 180,
      update: (age) => {
        const material = slash.material as THREE.MeshBasicMaterial;
        material.opacity = 1 - age;
        slash.scale.set(1 + age * 0.25, 1, 1 + age * 0.15);
      },
    });
    this.trimTransientMeshes();
  }

  private handlePulse(payload: { x: number; y: number; radius: number; color: number; durationMs: number }) {
    this.spawnRing(this.toWorld(payload.x, payload.y), payload.color, 0.2, Math.max(0.7, payload.radius / 20), payload.durationMs);
  }

  private spawnDamageNumber(x: number, y: number, amount: number, source?: PartnerType | 'relic') {
    const element = document.createElement('div');
    element.className = `three-damage-number ${amount >= 20 ? 'is-heavy' : ''}`;
    if (source === 'sword' || source === 'assassin' || amount >= 25) {
      element.classList.add('is-crit');
    }
    element.textContent = String(Math.max(1, Math.round(amount)));
    this.damageLayer.appendChild(element);
    const world = this.toWorld(x, y);
    world.y = 1.4;
    this.damageNumbers.push({
      element,
      world,
      bornAt: this.phaserScene.time.now,
      duration: 520,
      drift: 0.45 + Math.random() * 0.42,
    });
    while (this.damageNumbers.length > MAX_DAMAGE_NUMBERS) {
      const oldest = this.damageNumbers.shift();
      oldest?.element.remove();
    }
  }

  private spawnShard(origin: THREE.Vector3, color: number, index: number) {
    const mesh = new THREE.Mesh(
      new THREE.TetrahedronGeometry(0.12, 0),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 }),
    );
    const angle = index * 1.73;
    const distance = 0.4 + (index % 3) * 0.22;
    mesh.position.copy(origin);
    mesh.position.y = 0.45;
    this.root.add(mesh);
    this.transientMeshes.push({
      mesh,
      bornAt: this.phaserScene.time.now,
      duration: 380,
      update: (age) => {
        mesh.position.x = origin.x + Math.cos(angle) * distance * age;
        mesh.position.z = origin.z + Math.sin(angle) * distance * age;
        mesh.position.y = 0.45 + Math.sin(age * Math.PI) * 0.35;
        mesh.rotation.x += 0.12;
        mesh.rotation.y += 0.08;
        (mesh.material as THREE.MeshBasicMaterial).opacity = 1 - age;
      },
    });
    this.trimTransientMeshes();
  }

  private spawnRing(origin: THREE.Vector3, color: number, startScale: number, endScale: number, duration: number) {
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(0.88, 0.98, 48),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.58, side: THREE.DoubleSide }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.set(origin.x, 0.06, origin.z);
    ring.scale.setScalar(startScale);
    this.root.add(ring);
    this.transientMeshes.push({
      mesh: ring,
      bornAt: this.phaserScene.time.now,
      duration,
      update: (age) => {
        ring.scale.setScalar(THREE.MathUtils.lerp(startScale, endScale, age));
        (ring.material as THREE.MeshBasicMaterial).opacity = 0.58 * (1 - age);
      },
    });
    this.trimTransientMeshes();
  }

  private updateTransientMeshes(timeMs: number) {
    for (let i = this.transientMeshes.length - 1; i >= 0; i -= 1) {
      const entry = this.transientMeshes[i];
      const age = (timeMs - entry.bornAt) / entry.duration;
      if (age >= 1) {
        this.root.remove(entry.mesh);
        disposeObject(entry.mesh);
        this.transientMeshes.splice(i, 1);
        continue;
      }
      entry.update(Math.max(0, age), timeMs);
    }
  }

  private updateDamageNumbers(timeMs: number) {
    for (let i = this.damageNumbers.length - 1; i >= 0; i -= 1) {
      const entry = this.damageNumbers[i];
      const age = (timeMs - entry.bornAt) / entry.duration;
      if (age >= 1) {
        entry.element.remove();
        this.damageNumbers.splice(i, 1);
        continue;
      }
      this.tmp.copy(entry.world);
      this.tmp.y += age * entry.drift;
      this.tmp.project(this.camera);
      const x = (this.tmp.x * 0.5 + 0.5) * this.renderer.domElement.clientWidth;
      const y = (-this.tmp.y * 0.5 + 0.5) * this.renderer.domElement.clientHeight;
      entry.element.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -50%) scale(${1 + Math.sin(Math.min(1, age * 3) * Math.PI) * 0.18})`;
      entry.element.style.opacity = String(Math.max(0, 1 - age * 1.25));
    }
  }

  private updateHud(snapshot: ReturnType<RunState['snapshot']>, partners: PartnerVisualSnapshot[]) {
    this.materialValue.textContent = String(snapshot.materials);
    this.timerValue.textContent = String(Math.max(0, Math.ceil(snapshot.waveTimeLeft)));
    this.waveValue.textContent = `w${snapshot.wave}/${snapshot.maxWave > 0 ? snapshot.maxWave : '∞'}`;
    this.levelValue.textContent = String(snapshot.level);
    this.hpValue.textContent = `${Math.ceil(snapshot.hp)} / ${snapshot.maxHp}`;
    this.hpFill.style.width = `${Math.max(0, Math.min(100, (snapshot.hp / snapshot.maxHp) * 100))}%`;
    this.hud.dataset.phase = snapshot.phase;
    const visiblePartners = partners.slice(0, 6);
    const signature = visiblePartners.map((partner) => `${partner.type}:${partner.level}`).join('|');
    if (signature !== this.partnerRailSignature) {
      this.partnerRailSignature = signature;
      this.partnerRail.replaceChildren(...visiblePartners.map((partner) => {
        const node = document.createElement('span');
        node.className = 'three-partner-dot';
        node.style.setProperty('--partner-color', colorNumberToCss(partnerColors[partner.type]));
        node.textContent = String(partner.level);
        return node;
      }));
    }
  }

  private updateCamera(player: Phaser.GameObjects.Arc, _timeMs: number) {
    const playerWorld = this.toWorld(player.x, player.y);
    const target = new THREE.Vector3(playerWorld.x, 0.2, playerWorld.z + 2.0);
    this.cameraTarget.lerp(target, 0.18);
    const desired = new THREE.Vector3(this.cameraTarget.x, 17.8, this.cameraTarget.z + 20.2);
    this.cameraPosition.lerp(desired, 0.16);
    this.camera.position.copy(this.cameraPosition);
    this.camera.lookAt(this.cameraTarget.x, 0, this.cameraTarget.z - 2.0);
  }

  private hidePhaserWorld(state: BattleViewState) {
    state.player.setVisible(false);
    for (const enemy of state.enemies) enemy.setVisible(false);
    for (const partner of state.partners) partner.ref.setVisible(false);
    for (const projectile of state.projectiles) projectile.ref.setVisible(false);
    for (const drop of state.drops) drop.ref.setVisible(false);
  }

  private resize() {
    const app = document.getElementById('app');
    if (!app) {
      return;
    }
    const rect = app.getBoundingClientRect();
    const width = Math.max(1, rect.width);
    const height = Math.max(1, rect.height);
    this.renderer.setPixelRatio(resolveBattlePixelRatio());
    this.renderer.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.fov = width < height ? 52 : 50;
    this.camera.updateProjectionMatrix();
  }

  private toWorld(x: number, y: number) {
    const nx = (x / GAME_WIDTH - 0.5) * WORLD_WIDTH;
    const arenaY = Phaser.Math.Clamp((y - ARENA_TOP) / ARENA_HEIGHT, 0, 1);
    const z = (arenaY - 0.5) * WORLD_HEIGHT;
    return new THREE.Vector3(nx, 0, z);
  }

  private trimTransientMeshes() {
    while (this.transientMeshes.length > MAX_TRANSIENT_MESHES) {
      const oldest = this.transientMeshes.shift();
      if (!oldest) {
        continue;
      }
      this.root.remove(oldest.mesh);
      disposeObject(oldest.mesh);
    }
  }

  private clearDynamicMeshes() {
    for (const entry of this.enemyMeshes.values()) disposeObject(entry.group);
    for (const entry of this.partnerMeshes.values()) disposeObject(entry.group);
    for (const entry of this.dropMeshes.values()) disposeObject(entry.mesh);
    for (const entry of this.projectileMeshes.values()) disposeObject(entry.group);
    for (const entry of this.transientMeshes) disposeObject(entry.mesh);
    this.enemyMeshes.clear();
    this.partnerMeshes.clear();
    this.dropMeshes.clear();
    this.projectileMeshes.clear();
    this.transientMeshes.length = 0;
  }

  private publishMetrics() {
    window.__threeBattleMetrics = {
      pixelRatio: this.renderer.getPixelRatio(),
      calls: this.renderer.info.render.calls,
      triangles: this.renderer.info.render.triangles,
      geometries: this.renderer.info.memory.geometries,
      textures: this.renderer.info.memory.textures,
      enemies: this.enemyMeshes.size,
      partners: this.partnerMeshes.size,
      drops: this.dropMeshes.size,
      projectiles: this.projectileMeshes.size,
      transientMeshes: this.transientMeshes.length,
      damageNumbers: this.damageNumbers.length,
    };
  }
}

declare global {
  interface Window {
    __threeBattleMetrics?: ThreeBattleMetrics;
  }
}
